import React, { useCallback, useRef, useState } from "react"
import { CircularProgress, Grid } from "@material-ui/core"
import { TextField, Text, Portlet, Button, themeColors } from "letalkui"
import { useHistory, useParams } from "react-router-dom"

import {
	Loading,
	Breadcrumb,
	Notification
} from "@/components"

import ErrorHandlerService from "@/services/ErrorHandler"
import RoleService, { Role } from "@/services/RoleService"

import { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"

import AdminAccessRow from "@/pages/Admin/Role/RoleDetails/AdminAccessRow"
import PermissionGroupAccordion from "@/pages/Admin/Role/RoleDetails/PermissionGroupAccordion"
import { PERMISSION_MACRO_GROUPS, getFilteredPermissionMacroGroups } from "@/pages/Admin/Role/RoleDetails/permissionGroupsConfig"

import { PermissionEffect, Permissions } from "@/protocols/permission"

import { getStatusCode } from "@/utils/response"
import RoleGroupPermissionGate from "@/components/PermissionGate/RoleGroupPermissionGate"
import { useGlobalStateStore } from "@/store/GlobalState"

import useStyles from "@/pages/Admin/Role/RoleDetails/styles"

type RoleParams = {
	roleId?: string
}

const RoleDetails: React.FC = () => {
	const { roleId } = useParams<RoleParams>()
	const history = useHistory()

	const classes = useStyles()

	const globalStateStore = useGlobalStateStore()

	const [loading, setLoading] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [changed, setChanged] = useState(false)
	const [role, setRole] = useState<Role>({} as Role)

	const roleName = role.name
	const isSystemRole = role.type === "system"

	const markChanged = () => {
		setChanged(true)
	}

	const clearChanged = () => {
		setChanged(false)
	}

	const loadRole = useCallback(async () => {
		setLoading(true)

		try {
			const role = await RoleService.retrieveById(Number(roleId))

			setRole(role)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			history.push("/admin/attendance/roles")
		}

		setLoading(false)
	}, [roleId])

	const handleUpdateRole = async (role: Role) => {
		setIsSaving(true)
		try {
			await RoleService.update(role.id, role)
			Notification.success({ message: "Cargo atualizado com sucesso" })
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			const statusCode = getStatusCode(error as ErrorType)

			if (statusCode === 401) {
				Notification.error({ message: "Você não possui permissão para realizar essa ação." })
			} else {
				Notification.error({ message: "Erro ao atualizar cargo" })
			}
		}
		setIsSaving(false)
		clearChanged()
	}

	const handleNameChange = (value: string) => {
		setRole({ ...role, name: value })
		markChanged()
	}

	const handleDescriptionChange = (value: string) => {
		setRole({ ...role, description: value })
		markChanged()
	}

	const handleChangePermissions = (permissions: Permissions) => {
		setRole({ ...role, permissions })
		markChanged()
	}

	const isAdminLocked = (role.permissions?.system ?? []).find(permission => permission.action === "admin")?.effect === "allow"

	const previousPermissionsSnapshotRef = useRef<Permissions | null>(null)

	/**
	 * Turning it on snapshots the current permissions (restored if turned off again) and
	 * forces every action, across every feature, to "allow". If there's no snapshot when
	 * turning off (e.g. the role was already locked when the page loaded), only
	 * system:admin itself is reset - the rest is left as-is rather than guessing.
	 */
	const handleToggleAdminAccess = (checked: boolean) => {
		if (checked) {
			previousPermissionsSnapshotRef.current = role.permissions

			const updatedPermissions: Permissions = Object.fromEntries(
				Object.entries(role.permissions).map(([feature, items]) => [
					feature,
					items.map(permission => ({ ...permission, effect: "allow" as PermissionEffect }))
				])
			)

			handleChangePermissions(updatedPermissions)

			return
		}

		if (previousPermissionsSnapshotRef.current) {
			handleChangePermissions(previousPermissionsSnapshotRef.current)
			previousPermissionsSnapshotRef.current = null

			return
		}

		handleChangePermissions({
			...role.permissions,
			system: (role.permissions.system ?? []).map(permission => permission.action === "admin" ? { ...permission, effect: "deny" as PermissionEffect } : permission)
		})
	}

	const { checkedCount: totalCheckedCount, totalCount: totalActionsCount } = Object.entries(role?.permissions ?? {}).reduce((acc, [feature, items]) => {
		if (feature === "system") return acc

		acc.totalCount += items.length
		acc.checkedCount += items.filter(item => item.effect === "allow").length

		return acc
	}, { checkedCount: 0, totalCount: 0 })

	const filteredPermissionMacroGroups = getFilteredPermissionMacroGroups(globalStateStore.instance.current_channel_type)

	const [expandedMacroGroupIds, setExpandedMacroGroupIds] = useState<string[]>([PERMISSION_MACRO_GROUPS[0].id])

	const handleToggleMacroGroup = (id: string) => {
		setExpandedMacroGroupIds(currentIds => currentIds.includes(id) ? currentIds.filter(currentId => currentId !== id) : [...currentIds, id])
	}

	const areAllMacroGroupsExpanded = expandedMacroGroupIds.length === filteredPermissionMacroGroups.length

	const handleToggleExpandAllMacroGroups = () => {
		setExpandedMacroGroupIds(areAllMacroGroupsExpanded ? [] : filteredPermissionMacroGroups.map(group => group.id))
	}

	useDidMount(() => {
		loadRole()
	})

	return (
		<Loading loading={loading}>
			<Grid container direction="column" spacing={4}>
				<Grid item xs={12}>
					<Breadcrumb
						data={[
							{ name: "Cargos", pathname: "/admin/attendance/roles" }
						]}
					/>
				</Grid>
				<Grid item xs={12}>
					<Text.Title type="h1" fontWeight="bold">{roleName}</Text.Title>
				</Grid>
				<Grid item xs={12}>
					<Grid container direction="column" spacing={4}>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Text.Body type="default" fontWeight="bold">Configurações</Text.Body>
								</Grid>
								<Grid item xs={12}>
									<Portlet elevation={1}>
										<Grid container direction="column" spacing={3} className={classes.portletContent}>
											<Grid item xs={12}>
												<RoleGroupPermissionGate action="update">
													<TextField
														label="Nome do cargo"
														subLabel="Escreva aqui como este cargo será identificado"
														placeholder="Ex: Gestor de Automações"
														value={role.name}
														onChange={({ target }) => handleNameChange(target.value)}
														fullWidth
														type="text"
														disabled={isSystemRole}
													/>
												</RoleGroupPermissionGate>
											</Grid>
											<Grid item xs={12}>
												<RoleGroupPermissionGate action="update">
													<TextField
														label="Descrição"
														subLabel="Explique brevemente as responsabilidades deste cargo"
														placeholder="Ex: Usuário com acesso às automações do sistema"
														value={role?.description}
														onChange={({ target }) => handleDescriptionChange(target.value)}
														fullWidth
														multiline
														minRows={3}
														type="text"
													/>
												</RoleGroupPermissionGate>
											</Grid>
										</Grid>
									</Portlet>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Grid container justifyContent="space-between" alignItems="center">
										<Text.Body type="default" fontWeight="bold">Permissões</Text.Body>

										<Grid style={{ display: "flex", alignItems: "center", gap: "14px" }}>
											<Text.Body type="small2" color={themeColors.colors.system.gray[500]}>
												{totalCheckedCount}/{totalActionsCount} selecionadas
											</Text.Body>
											<button
												type="button"
												className={classes.expandAllButton}
												onClick={handleToggleExpandAllMacroGroups}
											>
												{areAllMacroGroupsExpanded ? "Recolher tudo" : "Expandir tudo"}
											</button>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Portlet elevation={1}>
										<Grid container direction="column" spacing={2} className={classes.portletContent}>
											{role.permissions && <Grid item xs={12}>
												<AdminAccessRow
													checked={isAdminLocked}
													onChange={handleToggleAdminAccess}
												/>
											</Grid>}

											{role.permissions && filteredPermissionMacroGroups.map(macroGroup => (
												<Grid item xs={12} key={macroGroup.id}>
													<PermissionGroupAccordion
														{...macroGroup}
														permissions={role.permissions}
														onChangePermission={handleChangePermissions}
														isExpanded={expandedMacroGroupIds.includes(macroGroup.id)}
														onToggle={handleToggleMacroGroup}
													/>
												</Grid>
											))}

											<Grid item xs={12}>
												<Grid container justifyContent="flex-end">
													<RoleGroupPermissionGate action="update">
														<Button.Primary
															size="large"
															onClick={() => handleUpdateRole(role)}
															endIcon={(loading || isSaving) && <CircularProgress size={20} />}
															disabled={!changed}
														>
															Salvar
														</Button.Primary>
													</RoleGroupPermissionGate>
												</Grid>
											</Grid>
										</Grid>
									</Portlet>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Loading>
	)
}

export default RoleDetails
